import { useState } from 'react';
import { Link, useNavigate, useRouter } from '@tanstack/react-router';
import { toast } from 'sonner';
import { APIError, api } from '@/lib/api';
import { AuthForm } from './AuthForm';
import { validateEmail, validatePassword } from './validation';

interface Errors {
  email?: string | undefined;
  password?: string | undefined;
}

export function LoginPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);

  function validate(): boolean {
    const next: Errors = {
      email: validateEmail(email),
      password: validatePassword(password),
    };
    setErrors(next);
    return !next.email && !next.password;
  }

  async function submit() {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.auth.login({ email: email.trim(), password });
      toast.success('Logged in successfully');
      // Re-run the root guard with the new session, then land on the dashboard.
      await router.invalidate();
      await navigate({ to: '/' });
    } catch (err) {
      const msg = err instanceof APIError ? err.detail : 'Login failed';
      // 401 means bad credentials — surface inline on the password field too.
      if (err instanceof APIError && err.status === 401) {
        setErrors((e) => ({ ...e, password: msg }));
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthForm
      title="Welcome back"
      subtitle="Log in to your Ghost dashboard"
      submitLabel="Log in"
      submitting={submitting}
      onSubmit={submit}
      fields={[
        {
          id: 'email',
          label: 'Email',
          type: 'email',
          value: email,
          onChange: (v) => {
            setEmail(v);
            setErrors((e) => ({ ...e, email: undefined }));
          },
          autoComplete: 'email',
          placeholder: 'you@example.com',
          error: errors.email,
          required: true,
        },
        {
          id: 'password',
          label: 'Password',
          type: 'password',
          value: password,
          onChange: (v) => {
            setPassword(v);
            setErrors((e) => ({ ...e, password: undefined }));
          },
          autoComplete: 'current-password',
          placeholder: '••••••••',
          error: errors.password,
          required: true,
        },
      ]}
      footer={
        <div className="space-y-2">
          <div>
            <Link to="/forgot-password" className="font-medium text-foreground hover:underline">
              Forgot password?
            </Link>
          </div>
          <div>
            Don&apos;t have an account?{' '}
            <Link to="/signup" className="font-medium text-foreground hover:underline">
              Sign up
            </Link>
          </div>
        </div>
      }
    />
  );
}
